/**
 * Hook for managing storage usage and quota
 *
 * Provides storage usage information and quota tracking.
 */

import { useState, useCallback, useEffect } from 'react';
import { api, StorageUsageResponse, FileOperationResponse } from '../api';

export interface UseStorageOptions {
  /** Auto-fetch on mount */
  autoFetch?: boolean;
  /** Refresh interval in milliseconds (0 to disable) */
  refreshInterval?: number;
}

export interface UseStorageResult {
  /** Storage usage information */
  usage: StorageUsageResponse | null;
  /** Recent operations */
  operations: FileOperationResponse[];
  /** Whether usage is loading */
  isLoading: boolean;
  /** Error if any */
  error: Error | null;
  /** Percentage of quota used */
  usedPercentage: number;
  /** Whether approaching quota limit (>80%) */
  isApproachingLimit: boolean;
  /** Whether at quota limit (>95%) */
  isAtLimit: boolean;
  /** Formatted total usage */
  totalFormatted: string;
  /** Formatted quota */
  quotaFormatted: string;
  /** Fetch storage usage */
  fetchUsage: () => Promise<void>;
  /** Fetch recent operations */
  fetchOperations: (options?: {
    fileId?: string;
    operationType?: 'upload' | 'download' | 'delete';
    limit?: number;
  }) => Promise<void>;
  /** Refresh all storage data */
  refresh: () => Promise<void>;
}

/**
 * Hook for managing storage usage and quota
 */
export function useStorage(options: UseStorageOptions = {}): UseStorageResult {
  const { autoFetch = true, refreshInterval = 0 } = options;

  const [usage, setUsage] = useState<StorageUsageResponse | null>(null);
  const [operations, setOperations] = useState<FileOperationResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const usedPercentage = usage?.usedPercentage ?? 0;
  const isApproachingLimit = usedPercentage >= 80;
  const isAtLimit = usedPercentage >= 95;
  const totalFormatted = usage?.totalFormatted ?? '0 B';
  const quotaFormatted = usage?.quotaFormatted ?? '0 B';

  const fetchUsage = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.getStorageUsage();
      setUsage(response);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch storage usage'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchOperations = useCallback(async (opts?: {
    fileId?: string;
    operationType?: 'upload' | 'download' | 'delete';
    limit?: number;
  }) => {
    try {
      const response = await api.listFileOperations({
        fileId: opts?.fileId,
        operationType: opts?.operationType,
        limit: opts?.limit ?? 20,
      });
      setOperations(response.operations);
    } catch (err) {
      // Don't overwrite usage error, just log
      console.error('Failed to fetch operations:', err);
    }
  }, []);

  const refresh = useCallback(async () => {
    await Promise.all([
      fetchUsage(),
      fetchOperations(),
    ]);
  }, [fetchUsage, fetchOperations]);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      refresh();
    }
  }, [autoFetch]);

  // Refresh interval
  useEffect(() => {
    if (refreshInterval > 0) {
      const interval = setInterval(refresh, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [refreshInterval, refresh]);

  return {
    usage,
    operations,
    isLoading,
    error,
    usedPercentage,
    isApproachingLimit,
    isAtLimit,
    totalFormatted,
    quotaFormatted,
    fetchUsage,
    fetchOperations,
    refresh,
  };
}

export default useStorage;
